import { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  Search,
  Loader2,
  AppWindow,
  Users,
  Lock,
  Filter,
  Play,
  Star,
} from 'lucide-react'
import { listAllApps, startApp, stopApp, rebuildApp, toggleFeaturedApp, type AdminApp } from '../../lib/api'
import AppActions from '../../components/AppActions'

const statusConfig = {
  running: { color: 'bg-green-500', label: 'Running' },
  stopped: { color: 'bg-gray-500', label: 'Stopped' },
  starting: { color: 'bg-amber-500', label: 'Starting' },
  error: { color: 'bg-red-500', label: 'Error' },
}

export default function AdminApps() {
  const navigate = useNavigate()
  const [apps, setApps] = useState<AdminApp[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')

  const loadApps = useCallback(async () => {
    try {
      const data = await listAllApps()
      setApps(data)
    } catch (err) {
      console.error('Failed to load apps:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadApps()
  }, [loadApps])

  async function handleStart(appName: string) {
    try {
      await startApp(appName)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to start app')
    }
    await loadApps()
  }

  async function handleStop(appName: string) {
    try {
      await stopApp(appName)
    } catch (err) {
      console.error('Failed to stop app:', err)
    }
    await loadApps()
  }

  async function handleRebuild(appName: string) {
    try {
      await rebuildApp(appName)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to rebuild app')
    }
    await loadApps()
  }

  async function handleToggleFeatured(appName: string) {
    try {
      await toggleFeaturedApp(appName)
    } catch (err) {
      console.error('Failed to toggle featured:', err)
    }
    await loadApps()
  }

  const filteredApps = apps.filter(app => {
    const matchesSearch = search === '' ||
      app.name.toLowerCase().includes(search.toLowerCase()) ||
      app.user_email.toLowerCase().includes(search.toLowerCase()) ||
      (app.user_name && app.user_name.toLowerCase().includes(search.toLowerCase()))
    const matchesStatus = statusFilter === 'all' || app.status === statusFilter
    return matchesSearch && matchesStatus
  })

  const stats = {
    total: apps.length,
    running: apps.filter(a => a.status === 'running').length,
    featured: apps.filter(a => a.is_featured).length,
    byAccess: {
      personal: apps.filter(a => a.access_level === 'personal').length,
      shared: apps.filter(a => a.access_level !== 'personal').length,
    },
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">All Apps</h1>
          <p className="text-muted-foreground">Manage deployed apps across all users</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-4 mb-6">
        <div className="p-4 rounded-xl border border-border bg-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <AppWindow className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className="text-xs text-muted-foreground">Total Apps</p>
            </div>
          </div>
        </div>
        <div className="p-4 rounded-xl border border-border bg-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
              <Play className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.running}</p>
              <p className="text-xs text-muted-foreground">Running</p>
            </div>
          </div>
        </div>
        <div className="p-4 rounded-xl border border-border bg-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
              <Star className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.featured}</p>
              <p className="text-xs text-muted-foreground">Featured</p>
            </div>
          </div>
        </div>
        <div className="p-4 rounded-xl border border-border bg-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <Lock className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.byAccess.personal}</p>
              <p className="text-xs text-muted-foreground">Personal</p>
            </div>
          </div>
        </div>
        <div className="p-4 rounded-xl border border-border bg-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
              <Users className="w-5 h-5 text-purple-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.byAccess.shared}</p>
              <p className="text-xs text-muted-foreground">Shared</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by app name or user..."
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">All Status</option>
            <option value="running">Running</option>
            <option value="stopped">Stopped</option>
            <option value="error">Error</option>
          </select>
        </div>
      </div>

      {/* Apps Table */}
      {filteredApps.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          {search || statusFilter !== 'all' ? 'No apps match your filters' : 'No apps have been created yet'}
        </div>
      ) : (
        <div className="border border-border rounded-xl overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead className="bg-secondary/50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium whitespace-nowrap">App</th>
                <th className="px-4 py-3 text-left text-sm font-medium whitespace-nowrap">Owner</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                <th className="px-4 py-3 text-center text-sm font-medium">Featured</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Created</th>
                <th className="px-4 py-3 text-right text-sm font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredApps.map((app) => {
                const status = statusConfig[app.status as keyof typeof statusConfig] || statusConfig.stopped

                return (
                  <tr
                    key={app.id}
                    className="hover:bg-secondary/30 cursor-pointer"
                    onClick={(e) => {
                      if ((e.target as HTMLElement).closest('button, a')) return
                      navigate(`/apps/${app.name}`)
                    }}
                  >
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium">{app.display_name || app.name}</p>
                        <p className="text-xs text-muted-foreground font-mono">/a/{app.name}/</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/admin/users/${app.user_id}`}
                        className="text-sm hover:text-primary"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {app.user_name || app.user_email}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${status.color}`} />
                        <span className="text-sm">{status.label}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleToggleFeatured(app.name) }}
                        className={`p-1.5 rounded-lg transition-colors ${
                          app.is_featured
                            ? 'text-amber-500 bg-amber-500/10 hover:bg-amber-500/20'
                            : 'text-muted-foreground/40 hover:text-amber-500 hover:bg-amber-500/5'
                        }`}
                        title={app.is_featured ? 'Remove from featured' : 'Add to featured'}
                      >
                        <Star className="w-4 h-4" fill={app.is_featured ? 'currentColor' : 'none'} />
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {new Date(app.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end">
                        <AppActions
                          app={app}
                          onStart={async () => handleStart(app.name)}
                          onStop={async () => handleStop(app.name)}
                          onRebuild={async () => handleRebuild(app.name)}
                          size="sm"
                        />
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
